import { Switch, Route, Router } from "wouter";
import { useBrowserLocation, useSearch } from "wouter/use-browser-location";
import { queryClient as browserQueryClient } from "./lib/queryClient";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Header from "@/components/Header";
import { ThemeProvider } from "@/contexts/ThemeContext";

import Landing from "@/pages/Landing";
import Chat from "@/pages/Chat";
import AboutUs from "@/pages/AboutUs";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import NotFound from "@/pages/not-found";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Profile from "@/pages/Profile";
import TheScience from "@/pages/TheScience";
import Waitlisted from "@/pages/Waitlisted";

interface AppProps {
  ssrPath?: string;
  queryClient?: QueryClient;
}

function AppRoutes() {
  return (
    <Switch>
      <Route path="/" component={Landing} />
      <Route path="/about" component={AboutUs} />
      <Route path="/science" component={TheScience} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms" component={Terms} />
      <Route path="/login" component={Login} />
      <Route path="/register" component={Register} />
      <Route path="/waitlisted" component={Waitlisted} />
      <Route path="/profile">
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      </Route>
      <Route path="/chat">
        <ProtectedRoute>
          <Chat />
        </ProtectedRoute>
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function AppContent() {
  return (
    <AuthProvider>
      <Header />
      <AppRoutes />
    </AuthProvider>
  );
}

function App({ ssrPath, queryClient }: AppProps) {
  const resolvedQueryClient = queryClient ?? browserQueryClient;
  const ssrSearch = ssrPath?.split("?")[1];
  const ssrHook = ssrPath ? () => useBrowserLocation({ ssrPath }) : undefined;
  const ssrSearchHook = ssrPath
    ? () => useSearch({ ssrSearch: ssrSearch ? `?${ssrSearch}` : "" })
    : undefined;

  return (
    <Router
      ssrPath={ssrPath}
      hook={ssrHook}
      searchHook={ssrSearchHook}
    >
      <ThemeProvider>
        <QueryClientProvider client={resolvedQueryClient}>
          <TooltipProvider>
            <Toaster />
            <AppContent />
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
